import React, { FunctionComponent, useEffect } from 'react';
import {
  Input,
  Group,
  FieldError,
  ToggleableField,
  ToggleableFieldProps,
  Select,
  FormHeader,
  Checkbox,
  Field,
  ResponsiveMargin,
  ResponsivePadding,
} from '../../components';
import { RegistrationFieldPermissionsField } from '@stax-core/types-omni';
import { BusinessFieldsProps } from './BusinessFields.types';
import { DateInput } from '../../components/DateInput/DateInput';
import { useWindowSize } from '../../hooks';
import { formatPhoneNumber, formatFederalTaxId } from '../../util/format.util';
import AddressAutocomplete from '../../components/AddressAutocomplete/AddressAutocomplete';
import { handleAddressAutocompleteSelect } from '../../util/addressAutocomplete.util';
import { companyTypes } from '../../constants/companyTypes';
import { FieldPermissionFlag } from '../../components/FieldPermissionFlag';
import {
  getStateLabel,
  getStateValues,
  getCountryOptions,
} from '@stax-core/lateralus-toolkit';

export const BusinessFields: FunctionComponent<BusinessFieldsProps> = props => {
  const { isMobile } = useWindowSize();
  const businessCountryValue = props.values.business_address_country;
  const businessLocationCountryValue =
    props.values.business_location_address_country;

  const isBusinessAddressStateVisible =
    ['USA', 'CAN'].includes(businessCountryValue!) || !businessCountryValue;
  const isBusinessLocationAddressStateVisible =
    ['USA', 'CAN'].includes(businessLocationCountryValue!) ||
    !businessLocationCountryValue;

  const mutators = props.form.mutators;

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  const stateBusinessLabel = getStateLabel(businessCountryValue!);
  const stateBusinessLocationLabel = getStateLabel(
    businessLocationCountryValue!,
  );
  const stateBusinessValues = getStateValues(businessCountryValue!);
  const stateBusinessLocationValues = getStateValues(
    businessLocationCountryValue!,
  );

  const countriesAsOptions = [{ label: 'Select One', value: '' }].concat(
    getCountryOptions(),
  );

  // If user changes country to non-USA we need to clear the state field value
  useEffect(() => {
    if (
      businessCountryValue &&
      !['USA', 'CAN'].includes(businessCountryValue)
    ) {
      mutators.setFieldValue('business_address_state', '');
    }
  }, [mutators, businessCountryValue]);

  useEffect(() => {
    if (
      businessLocationCountryValue &&
      !['USA', 'CAN'].includes(businessLocationCountryValue)
    ) {
      mutators.setFieldValue('business_location_address_state', '');
    }
  }, [mutators, businessLocationCountryValue]);

  return (
    <>
      {props.mode === 'review' && (
        <FormHeader
          data-testid="review-business"
          tag="h3"
          bordered
          style={{ marginTop: 0, paddingTop: 0 }}
        >
          Business Details
        </FormHeader>
      )}

      <ResponsivePadding>
        <Group direction="column" space="1rem/0px">
          <Group direction="column">
            <ToggleableField
              name="business_legal_name"
              label="Legal Business Name"
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} autoFocus />}
            </ToggleableField>
            <FieldError name="business_legal_name" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="business_dba"
              label="DBA"
              {...defaultFieldProps}
            >
              {props => (
                <Input
                  {...props.input}
                  tooltip="If your DBA (Doing Business As) name is the same as your legal business name, please enter the legal business name into the DBA name field. The DBA name is what will show on a cardholder’s bank statement."
                />
              )}
            </ToggleableField>
            <FieldError name="business_dba" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="business_website"
              label="Website (optional)"
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} />}
            </ToggleableField>
            <FieldError name="business_website" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="business_email"
              label="Business Email"
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} type="email" />}
            </ToggleableField>
            <FieldError name="business_email" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="business_location_phone_number"
              label="Business Phone Number"
              parse={formatPhoneNumber}
              format={formatPhoneNumber}
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} type="tel" maxLength={14} />}
            </ToggleableField>
            <FieldError name="business_location_phone_number" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="business_fax"
              label="Fax (optional)"
              parse={formatPhoneNumber}
              format={formatPhoneNumber}
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} type="tel" maxLength={14} />}
            </ToggleableField>
            <FieldError name="business_fax" />
          </Group>

          <Group direction={isMobile ? 'column-reverse' : 'row'}>
            <Group direction="column" space="1rem/0px" style={{ flex: '50%' }}>
              <Group direction="column">
                <ToggleableField
                  name="business_tax_id"
                  label="Tax ID/EIN"
                  parse={formatFederalTaxId}
                  format={formatFederalTaxId}
                  {...defaultFieldProps}
                >
                  {props => (
                    <Input
                      {...props.input}
                      tooltip="Tax ID/EIN is required but under some circumstances an applicant may use their SSN instead."
                      // 9 digits + 1 hyphen = 10 chars
                      maxLength={10}
                    />
                  )}
                </ToggleableField>
                <FieldError name="business_tax_id" />
              </Group>

              <Group direction="column">
                <ToggleableField
                  name="business_open_date"
                  label="Business Incorporation Date"
                  {...defaultFieldProps}
                >
                  {props => <DateInput {...props.input} />}
                </ToggleableField>
                <FieldError name="business_open_date" />
              </Group>

              <Group direction="column">
                <ToggleableField
                  name="company_type"
                  label="Company Type"
                  {...defaultFieldProps}
                  type="select"
                  options={companyTypes}
                  optionLabelKey="name"
                  optionValueKey="value"
                >
                  {props => (
                    <Select {...props.input}>
                      <option value="" disabled />
                      {companyTypes.map((companyType, index) => (
                        <option key={index} value={companyType.value}>
                          {companyType.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </ToggleableField>
                <FieldError name="company_type" />
              </Group>
            </Group>
          </Group>
        </Group>
      </ResponsivePadding>

      <FieldPermissionFlag
        fields={
          [
            'business_location_address_1',
            'business_location_address_city',
            'business_location_address_state',
            'business_location_address_zip',
            'business_location_address_country',
          ] as RegistrationFieldPermissionsField[]
        }
        renderIf="atLeastOneRead"
      >
        <FormHeader tag="h3" bordered>
          Business Location
        </FormHeader>

        <ResponsivePadding>
          <Group direction="column" space="1rem/0px">
            <Group direction="column">
              <FieldPermissionFlag
                fields={[
                  'business_location_address_city',
                  'business_location_address_state',
                  'business_location_address_zip',
                  'business_location_address_country',
                ]}
              >
                {({ atLeastOneNone }) => (
                  <ToggleableField
                    name="business_location_address_1"
                    data-testid="business_location_address_1"
                    label="Location Address Line 1"
                    {...defaultFieldProps}
                  >
                    {props => (
                      <AddressAutocomplete
                        onSelect={data =>
                          handleAddressAutocompleteSelect(
                            data,
                            {
                              streetAddress: 'business_location_address_1',
                              city: 'business_location_address_city',
                              state: 'business_location_address_state',
                              zip: 'business_location_address_zip',
                              country: 'business_location_address_country',
                            },
                            mutators.setFieldValue,
                          )
                        }
                        inputProps={{
                          ...props.input,
                          tooltip:
                            'Business Address field should be the address listed on your business license or business utility bill.',
                        }}
                        onChange={value =>
                          mutators.setFieldValue(
                            'business_location_address_1',
                            value,
                          )
                        }
                        // IF address_city, address_state, address_zip, or address_country fields are not visible,
                        // THEN disable the autocomplete
                        // since we do not want to allow the user to attempt to change non-visible (possibly prefilled) data
                        disableAutocomplete={atLeastOneNone}
                      />
                    )}
                  </ToggleableField>
                )}
              </FieldPermissionFlag>
              <FieldError name="business_location_address_1" />
            </Group>

            {/* if business_location_address_1 is not readable, also hide business_location_address_2 */}
            <FieldPermissionFlag field="business_location_address_1">
              <Group direction="column">
                <ToggleableField
                  name="business_location_address_2"
                  label="Location Address Line 2 (optional)"
                  {...defaultFieldProps}
                >
                  {props => <Input {...props.input} />}
                </ToggleableField>
                <FieldError name="business_location_address_2" />
              </Group>
            </FieldPermissionFlag>

            <Group direction={isMobile ? 'column' : 'row'} space="0.5rem/0px">
              <Group direction="column" style={{ flex: '40%' }}>
                <ToggleableField
                  name="business_location_address_city"
                  label="City"
                  {...defaultFieldProps}
                >
                  {props => <Input {...props.input} />}
                </ToggleableField>
                <FieldError name="business_location_address_city" />
              </Group>

              {isBusinessLocationAddressStateVisible && (
                <Group direction="column" style={{ flex: '30%' }}>
                  <ToggleableField
                    name="business_location_address_state"
                    label={stateBusinessLocationLabel}
                    {...defaultFieldProps}
                    type="select"
                    optionLabelKey="name"
                    optionValueKey="abbr"
                    options={stateBusinessLocationValues}
                  >
                    {props => {
                      return (
                        <Select {...props.input}>
                          <option value="" disabled />
                          {stateBusinessLocationValues.map((state, index) => (
                            <option key={index} value={state.abbr}>
                              {state.name}
                            </option>
                          ))}
                        </Select>
                      );
                    }}
                  </ToggleableField>
                  <FieldError name="business_location_address_state" />
                </Group>
              )}

              <Group direction="column">
                <ToggleableField
                  name="business_location_address_zip"
                  label="Zip"
                  {...defaultFieldProps}
                >
                  {props => <Input {...props.input} />}
                </ToggleableField>
                <FieldError name="business_location_address_zip" />
              </Group>
              <Group direction="column">
                <ToggleableField
                  name="business_location_address_country"
                  label="Country"
                  {...defaultFieldProps}
                  type="select"
                  options={countriesAsOptions}
                  optionLabelKey="label"
                  optionValueKey="value"
                >
                  {props => (
                    <Select {...props.input}>
                      <option value="" disabled />
                      {countriesAsOptions.map((country, index) => (
                        <option key={index} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </ToggleableField>
                <FieldError name="business_location_address_country" />
              </Group>
            </Group>

            <FieldPermissionFlag
              fields={
                [
                  'business_location_address_1',
                  'business_location_address_city',
                  'business_location_address_zip',
                  'business_location_address_country',
                ] as RegistrationFieldPermissionsField[]
              }
              // only show business_address_* fields if at least one
              // of the necessary business_location_address_* fields are readable
              renderIf="atLeastOneRead"
            >
              <FieldPermissionFlag
                fields={
                  [
                    'business_address_1',
                    'business_address_2',
                    'business_address_zip',
                    'business_address_country',
                  ] as RegistrationFieldPermissionsField[]
                }
                // only show this checkbox if at least one of these fields are writeable
                renderIf="atLeastOneWrite"
              >
                <Group direction="row">
                  <ResponsiveMargin mobile="1rem 0.5rem" desktop="0">
                    <Field
                      name="hasSameBillingAndLocationAddress"
                      type="checkbox"
                    >
                      {props => (
                        <Checkbox
                          {...props.input}
                          id="hasSameBillingAndLocationAddress"
                          label="Billing information is same as business information"
                        />
                      )}
                    </Field>
                  </ResponsiveMargin>
                </Group>
              </FieldPermissionFlag>
              {!props.values.hasSameBillingAndLocationAddress && (
                <>
                  <FormHeader tag="h3" bordered>
                    Billing Information
                  </FormHeader>

                  <Group direction="column">
                    <FieldPermissionFlag
                      fields={[
                        'business_address_city',
                        'business_address_state',
                        'business_address_zip',
                        'business_address_country',
                      ]}
                    >
                      {({ atLeastOneNone }) => (
                        <ToggleableField
                          name="business_address_1"
                          label="Billing Address Line 1"
                          {...defaultFieldProps}
                        >
                          {props => (
                            <AddressAutocomplete
                              onSelect={data =>
                                handleAddressAutocompleteSelect(
                                  data,
                                  {
                                    streetAddress: 'business_address_1',
                                    city: 'business_address_city',
                                    state: 'business_address_state',
                                    zip: 'business_address_zip',
                                    country: 'business_address_country',
                                  },
                                  mutators.setFieldValue,
                                )
                              }
                              inputProps={{ ...props.input }}
                              onChange={value =>
                                mutators.setFieldValue(
                                  'business_address_1',
                                  value,
                                )
                              }
                              // IF address_city, address_state, address_zip, or address_country fields are not visible,
                              // THEN disable the autocomplete
                              // since we do not want to allow the user to attempt to change non-visible (possibly prefilled) data
                              disableAutocomplete={atLeastOneNone}
                            />
                          )}
                        </ToggleableField>
                      )}
                    </FieldPermissionFlag>
                    <FieldError name="business_address_1" />
                  </Group>

                  {/* if business_address_1 is not readable, also hide business_address_2 */}
                  <FieldPermissionFlag field="business_address_1">
                    <Group direction="column">
                      <ToggleableField
                        name="business_address_2"
                        label="Billing Address Line 2 (optional)"
                        {...defaultFieldProps}
                      >
                        {props => <Input {...props.input} />}
                      </ToggleableField>
                      <FieldError name="business_address_2" />
                    </Group>
                  </FieldPermissionFlag>

                  <Group
                    direction={isMobile ? 'column' : 'row'}
                    space="0.5rem/0px"
                  >
                    <Group direction="column" style={{ flex: '40%' }}>
                      <ToggleableField
                        name="business_address_city"
                        label="City"
                        {...defaultFieldProps}
                      >
                        {props => <Input {...props.input} />}
                      </ToggleableField>
                      <FieldError name="business_address_city" />
                    </Group>
                    {isBusinessAddressStateVisible && (
                      <Group direction="column" style={{ flex: '30%' }}>
                        <ToggleableField
                          name="business_address_state"
                          label={stateBusinessLabel}
                          {...defaultFieldProps}
                          type="select"
                          options={stateBusinessValues}
                          optionLabelKey="name"
                          optionValueKey="abbr"
                        >
                          {props => (
                            <Select {...props.input}>
                              <option value="" disabled />
                              {stateBusinessValues.map((state, index) => (
                                <option key={index} value={state.abbr}>
                                  {state.name}
                                </option>
                              ))}
                            </Select>
                          )}
                        </ToggleableField>
                        <FieldError name="business_address_state" />
                      </Group>
                    )}

                    <Group direction="column">
                      <ToggleableField
                        name="business_address_zip"
                        label="Zip"
                        {...defaultFieldProps}
                      >
                        {props => <Input {...props.input} />}
                      </ToggleableField>
                      <FieldError name="business_address_zip" />
                    </Group>
                    <Group direction="row">
                      <ToggleableField
                        name="business_address_country"
                        label="Country"
                        {...defaultFieldProps}
                        type="select"
                        options={countriesAsOptions}
                        optionLabelKey="label"
                        optionValueKey="value"
                      >
                        {props => (
                          <Select {...props.input}>
                            <option value="" disabled />
                            {countriesAsOptions.map((country, index) => (
                              <option key={index} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </Select>
                        )}
                      </ToggleableField>
                      <FieldError name="business_address_country" />
                    </Group>
                  </Group>
                  <Group direction="column">
                    <ToggleableField
                      name="business_phone_number"
                      label="Billing Address Phone Number (optional)"
                      parse={formatPhoneNumber}
                      format={formatPhoneNumber}
                      {...defaultFieldProps}
                    >
                      {props => (
                        <Input {...props.input} type="tel" maxLength={14} />
                      )}
                    </ToggleableField>
                    <FieldError name="business_phone_number" />
                  </Group>
                </>
              )}
            </FieldPermissionFlag>
          </Group>
        </ResponsivePadding>
      </FieldPermissionFlag>
      {props.children}
    </>
  );
};
